import {
  arrayOf, bool, number, shape, string
} from 'prop-types';

import { FULFILLMENT_METHODS, CUSTOMER_TYPES, INVENTORY_SEARCH_ARRAY } from '../../components/constants';
import {
  bopis, boss, sth, safelyGet, getDeliveryZip
} from '../../components/helper/utils';

export const productShape = shape({
  details: shape({
    collection: shape({
      name: string,
      url: string,
      type: string
    })
  }),
  media: {
    images: arrayOf(shape({
      subType: string,
      url: string,
    })),
  },
  identifiers: shape({
    parentId: string,
    itemId: string,
    productLabel: string,
    canonicalUrl: string,
    productUrl: string
  }),
  availabilityType: shape({
    discontinued: bool
  }),
  fulfillment: shape({}),
  pricing: shape({
    unitOfMeasure: string,
    alternatePriceDisplay: bool,
    specialBuy: number,
    promotion: shape({
      dollarOff: number,
      percentageOff: number,
      // lowerPrice: bool
    }),
    alternate: shape({
      unit: shape({
        unitsPerCase: number,
        unitsOriginalPrice: number,
      })
    }),
  }),
  reviews: shape({
    ratingsReviews: shape({
      averageRating: string,
      totalReviews: string
    }),
  })
});

export const padWithZeros = (n) => {
  if (n
    && n.length
    && n.length === 4) {
    return n;
  }
  let storeNumber = '' + n;
  while (storeNumber.length < 4) {
    storeNumber = '0' + storeNumber;
  }
  return storeNumber;
};

export const isPickupService = (fulfillmentOption) => {
  return fulfillmentOption?.type === 'pickup';
};

export const isDeliveryService = (fulfillmentOption) => {
  return fulfillmentOption?.type === 'delivery';
};

export const isBODFS = (product) => {
  const deliveryService = (product?.fulfillment?.fulfillmentOptions || []).find(isDeliveryService);
  const expressFulfillment = (deliveryService?.services || []).find((service) => service.type === 'express delivery');

  return !!expressFulfillment;
};

export const isBOPIS = (product) => {
  const pickupService = (product?.fulfillment?.fulfillmentOptions || []).find(isPickupService);
  const bopisFulfillment = (pickupService?.services || []).find((service) => service.type === 'bopis');

  return !!bopisFulfillment;
};

export const isBrowseOnly = (product) => {
  const fulfillable = !!((product?.fulfillment?.fulfillmentOptions || []).find((option) => option.fulfillable));
  const availableOnline = product?.availabilityType?.type === 'Browse Only';

  return availableOnline && !fulfillable;
};

export const isCurrentlyUnavailable = (product) => {
  return !((product?.fulfillment?.fulfillmentOptions || [])
    .find((option) => (option.services || [])
      .find((service) => (service.locations || [])
        .find((location) => location.inventory?.isInStock))));
};

export const isDiscontinued = (product) => {
  return !!(product?.availabilityType?.discontinued);
};

export const isOutOfStockOnline = (product = {}) => {
  const { dataSources, fulfillment, availabilityType } = product;
  const deliveryService = (fulfillment?.fulfillmentOptions || []).find(isDeliveryService);
  const shippingFulfillment = (deliveryService?.services || []).find((service) => service.type === 'sth');
  const locations = shippingFulfillment?.locations;
  if (dataSources === 'catalog' && !fulfillment?.fulfillmentOptions && availabilityType?.type === 'Online') {
    return true;
  }
  if (locations?.length) {
    return locations[0]?.inventory?.isOutOfStock && !deliveryService?.fulfillable;
  }
  return false;
};

export const isSTH = (product) => {
  const deliveryService = (product?.fulfillment?.fulfillmentOptions || []).find(isDeliveryService);
  const shippingFulfillment = (deliveryService?.services || []).find((service) => service.type === 'sth');
  // This code snippet to handle sth edgecase where item is oos and fulfillbale true.
  let isSthOOS = false;
  const locations = shippingFulfillment?.locations;
  if (locations?.length) {
    isSthOOS = locations[0]?.inventory?.isOutOfStock;
  }
  return !!shippingFulfillment && deliveryService?.fulfillable && !isSthOOS;
};

export const isBOSS = (product) => {
  const pickupService = (product?.fulfillment?.fulfillmentOptions || []).find(isPickupService);
  const bossFulfillment = (pickupService?.services || []).find((service) => service.type === 'boss');

  return !!bossFulfillment;
};

export const isAppliance = (product) => {
  return (product?.identifiers?.productType?.toUpperCase() === 'MAJOR_APPLIANCE');
};

export const applianceDeliveryThreshold = () => {
  return 396;
};

export const meetsApplianceDeliveryThreshold = (product) => {
  return product?.pricing?.value >= applianceDeliveryThreshold();
};

export const isLiveGoods = (product) => {
  return !!(product?.info?.isLiveGoodsProduct);
};

export const isFulfillable = (product) => {
  return !!(product?.fulfillment?.fulfillmentOptions || []).find((option) => option.fulfillable);
};

export const isFulfillableIRGSku = (product) => {
  const {
    buyable, discontinued, status
  } = product?.availabilityType || {};

  return buyable && status && !discontinued;
};

export const isInStock = (product) => {
  const deliveryService = (product?.fulfillment?.fulfillmentOptions || []).find(isDeliveryService);
  const shippingFulfillment = (deliveryService?.services || []).find((service) => service.type === 'sth');

  const locations = shippingFulfillment?.locations;

  if (locations?.length) {
    return !!(locations[0]?.inventory?.isInStock);
  }

  return false;
};

export const isInStockYourStore = (product) => {
  const pickupService = (product?.fulfillment?.fulfillmentOptions || []).find(isPickupService);
  const bopisFulfillment = (pickupService?.services || []).find((service) => service.type === 'bopis');

  const locations = bopisFulfillment?.locations || [];

  return !!(locations.find(
    (location) => location.isAnchor && location.inventory?.isInStock)
  );
};

export const isInStockNearby = (product) => {
  const pickupService = (product?.fulfillment?.fulfillmentOptions || []).find(isPickupService);
  const bopisFulfillment = (pickupService?.services || []).find((service) => service.type === 'bopis');

  const locations = bopisFulfillment?.locations || [];
  return !!(locations.find(
    (location) => !location.isAnchor && location.inventory?.isInStock)
  );
};

export const isLimitedStock = (product) => {
  const pickupService = (product?.fulfillment?.fulfillmentOptions || []).find(isPickupService);
  const bopisFulfillment = (pickupService?.services || []).find((service) => service.type === 'bopis');

  const locations = bopisFulfillment?.locations || [];

  return !!(locations.find(
    (location) => location.isAnchor && location.inventory?.isLimitedQuantity)
  );
};

export const isLimitedStockNearby = (product) => {
  const pickupService = (product?.fulfillment?.fulfillmentOptions || []).find(isPickupService);
  const bopisFulfillment = (pickupService?.services || []).find((service) => service.type === 'bopis');

  const locations = bopisFulfillment?.locations || [];

  return !!(locations.find(
    (location) => !location.isAnchor && location.inventory?.isLimitedQuantity)
  );
};

export const isOutOfStockYourStore = (product) => {
  const pickupService = (product?.fulfillment?.fulfillmentOptions || []).find(isPickupService);
  const bopisFulfillment = (pickupService?.services || []).find((service) => service.type === 'bopis');

  const locations = bopisFulfillment?.locations || [];

  return !!(locations.find(
    (location) => location.isAnchor && location.inventory?.isOutOfStock)
  );
};

export const isOutOfStockBoss = (product) => {
  const pickupService = (product?.fulfillment?.fulfillmentOptions || []).find(isPickupService);
  const bossFulfillment = (pickupService?.services || []).find((service) => service.type === 'boss');

  const locations = bossFulfillment?.locations || [];

  return !!(locations.find(
    (location) => location.inventory?.isOutOfStock)
  );
};

export const getNearbyStoreWithLimitedStock = (product) => {
  const pickupService = (product?.fulfillment?.fulfillmentOptions || []).find(isPickupService);
  const bopisFulfillment = (pickupService?.services || []).find((service) => service.type === 'bopis');

  const locations = bopisFulfillment?.locations || [];

  const store = locations.find(
    (location) => !location.isAnchor && location.inventory?.isLimitedQuantity
  );

  return store;
};

export const getNearbyStoreInStock = (product) => {
  const pickupService = (product?.fulfillment?.fulfillmentOptions || []).find(isPickupService);
  const bopisFulfillment = (pickupService?.services || []).find((service) => service.type === 'bopis');

  const locations = bopisFulfillment?.locations || [];

  const store = locations.find(
    (location) => !location.isAnchor && location.inventory?.isInStock
  );

  return store;
};

export const isBuyInStore = (product) => {
  const pickupService = (product.fulfillment?.fulfillmentOptions || []).find(isPickupService);
  return pickupService && !pickupService?.fulfillable;
};

export const getFulfillmentMethod = (product, hasInStoreFilter = false) => {
  if (hasInStoreFilter && (isInStockYourStore(product) || isInStockNearby(product)) && !isBuyInStore(product)) {
    return FULFILLMENT_METHODS.BOPIS;
  }
  if (isAppliance(product)) {
    return FULFILLMENT_METHODS.APPLIANCE;
  }
  if (isSTH(product)) {
    return FULFILLMENT_METHODS.STH;
  }
  if (isBOPIS(product)) {
    return FULFILLMENT_METHODS.BOPIS;
  }
  if (isBOSS(product)) {
    return FULFILLMENT_METHODS.STORE;
  }
  if (isBODFS(product)) {
    return FULFILLMENT_METHODS.BODFS;
  }
  return FULFILLMENT_METHODS.STH;
};

export const formatDistance = (distance = 0) => {
  return distance.toFixed(1);
};

export const getSellableQuantityYourStore = (product) => {
  const pickupService = (product?.fulfillment?.fulfillmentOptions || []).find(isPickupService);
  const bopisFulfillment = (pickupService?.services || []).find((service) => service.type === 'bopis');

  const locations = bopisFulfillment?.locations || [];

  const store = locations.find(
    (location) => location.isAnchor
  );

  return store?.inventory?.quantity || 0;
};

export const getSellableQuantityNearbyStore = (product) => {
  const pickupService = (product?.fulfillment?.fulfillmentOptions || []).find(isPickupService);
  const bopisFulfillment = (pickupService?.services || []).find((service) => service.type === 'bopis');

  const locations = bopisFulfillment?.locations || [];

  const store = locations.find(
    (location) => !location.isAnchor && location.inventory?.isInStock
  );

  return store?.inventory?.quantity || 0;
};

export const hasFreeShipping = (product) => {
  const deliveryService = (product?.fulfillment?.fulfillmentOptions || []).find(isDeliveryService);
  const shippingFulfillment = (deliveryService?.services || []).find((service) => service.type === 'sth');

  return shippingFulfillment?.hasFreeShipping;
};

export const getShippingThreshold = (product) => {
  const deliveryService = (product?.fulfillment?.fulfillmentOptions || []).find(isDeliveryService);
  const shippingFulfillment = (deliveryService?.services || []).find((service) => service.type === 'sth');

  return shippingFulfillment?.freeDeliveryThreshold;
};

export const shouldShowShippingThreshold = (product, threshold) => {
  return product?.pricing?.value < threshold;
};

export const isBackordered = (product) => {
  return !!(product?.fulfillment?.backordered);
};

export const isBackorderedWithDate = (product) => {
  return !!(product?.fulfillment?.backordered && product?.fulfillment?.backorderedShipDate);
};

export const isSameDayDelivery = (product) => {
  const deliveryService = (product?.fulfillment?.fulfillmentOptions || []).find(isDeliveryService);
  const shippingFulfillment = (deliveryService?.services || []).find((service) => service.type === 'express delivery');

  return shippingFulfillment?.deliveryTimeline === 'TODAY';
};

export const isNextDayDelivery = (product) => {
  // @TODO: Check that this is showing correctly for stage: 303780504
  const deliveryService = (product?.fulfillment?.fulfillmentOptions || []).find(isDeliveryService);
  const shippingFulfillment = (deliveryService?.services || []).find((service) => service.type === 'express delivery');

  return shippingFulfillment?.deliveryTimeline === 'TOMORROW';
};

const getInventory = (service) => {
  return service?.locations?.[0]?.inventory?.quantity || 0;
};

const getLocationFromProduct = (product, ffmMethod) => {
  const ffmTypes = INVENTORY_SEARCH_ARRAY[ffmMethod];
  return safelyGet(() => (product.fulfillment.fulfillmentOptions)
    .find((option) => (option.type === ffmTypes[0] && option.services)).services
    .find((services) => (services.type === ffmTypes[1] && services.locations)).locations);
};

export const getAllFulfillmentInventories = (product) => {
  const fulfillmentMethods = {
    [FULFILLMENT_METHODS.STH]: 0,
    [FULFILLMENT_METHODS.BOPIS]: 0,
    [FULFILLMENT_METHODS.STORE]: 0,
  };

  if (isSTH(product)) {
    fulfillmentMethods[FULFILLMENT_METHODS.STH] += getInventory(sth(product?.fulfillment));
  }

  if (isBOPIS(product)) {
    fulfillmentMethods[FULFILLMENT_METHODS.BOPIS] += getInventory(bopis(product?.fulfillment));
  }

  if (isBOSS(product)) {
    fulfillmentMethods[FULFILLMENT_METHODS.STORE] += getInventory(boss(product?.fulfillment));
  }

  return fulfillmentMethods;
};

export const getDisplayableInventory = (product, customer, ffmMethod) => {
  const eligibleFulfillments = [
    FULFILLMENT_METHODS.STH,
    FULFILLMENT_METHODS.BODFS,
    FULFILLMENT_METHODS.BOSS
  ];

  const eligibleCustomerType = [
    CUSTOMER_TYPES.B2B
  ];

  const validFulfillment = eligibleFulfillments.includes(ffmMethod);
  const validCustomerType = eligibleCustomerType.includes(customer?.type?.toUpperCase());
  const locationObject = getLocationFromProduct(product, ffmMethod);

  const displayableQty = validFulfillment && validCustomerType && !isBackordered(product)
  && locationObject?.[0]?.inventory?.quantity;

  return (displayableQty) || 0;
};

export const isShowBuyInStoreNearBy = (product) => {
  const pickupService = (product?.fulfillment?.fulfillmentOptions || []).find(isPickupService);
  const bopisFulfillment = (pickupService?.services || []).find((service) => service.type === 'bopis');

  const locations = bopisFulfillment?.locations || [];

  return locations.find(
    (location) => location?.isAnchor && location?.isBuyInStoreCheckNearBy
  );
};

export const getDeliveryZipCookie = () => {
  return getDeliveryZip();
};